/**
 * NumericFilterPopover.jsx
 *
 * Drop-in cousin of DateFilterPopover for numeric columns (Quoted, COGS,
 * Profit, PO $, Invoiced, $ Leftover, Margin %). Same anchor/positioning
 * behaviour as the other popovers — walks up to the column's <th>, closes
 * on outside click / Escape, reposition on scroll (all provided by
 * FilterPopoverShell).
 *
 * Filter shape: { min?: number, max?: number } — or null when no filter.
 * Comparison is numeric: rows that have an undefined value are dropped
 * when any bound is set.
 *
 * Quick "above / below" presets help finance scan typical thresholds:
 *   ≥ $10K   < $10K
 *   ≥ $100K  < $100K
 *   ≥ $1M    < $1M
 */
import { useState } from 'react';
import { FilterPopoverShell } from './FilterPopoverShell';

const PRESETS = [
  { label: '≥ $10K', min: 10_000, max: null },
  { label: '< $10K', min: null, max: 10_000 },
  { label: '≥ $100K', min: 100_000, max: null },
  { label: '< $100K', min: null, max: 100_000 },
  { label: '≥ $1M', min: 1_000_000, max: null },
  { label: '< $1M', min: null, max: 1_000_000 },
];

const PCT_PRESETS = [
  { label: '≥ 30%', min: 30, max: null },
  { label: '20–30%', min: 20, max: 30 },
  { label: '< 20%', min: null, max: 20 },
  { label: '≥ 0', min: 0, max: null },
];

export function NumericFilterPopover({ columnLabel, selected, onApply, onSort, sortDir, onClose, kind = 'money' }) {
  const [min, setMin] = useState(selected?.min != null ? String(selected.min) : '');
  const [max, setMax] = useState(selected?.max != null ? String(selected.max) : '');
  const presets = kind === 'percent' ? PCT_PRESETS : PRESETS;

  const setNumeric = (setter) => (e) => setter(e.target.value.replace(/[^0-9.\-]/g, ''));

  const apply = () => {
    let lo = min === '' ? null : parseFloat(min);
    let hi = max === '' ? null : parseFloat(max);
    if (lo == null && hi == null) {
      onApply?.(null);
      onClose?.();
      return;
    }
    if (lo != null && hi != null && lo > hi) {
      // swap so user-friendly: min/max can be entered in any order
      [lo, hi] = [hi, lo];
    }
    onApply?.({ min: lo, max: hi });
    onClose?.();
  };
  const clear = () => {
    setMin('');
    setMax('');
    onApply?.(null);
    onClose?.();
  };

  const pick = (p) => {
    setMin(p.min == null ? '' : String(p.min));
    setMax(p.max == null ? '' : String(p.max));
  };

  const symbol = kind === 'percent' ? '%' : '$';

  return (
    <FilterPopoverShell
      width={280}
      sortAscLabel="Sort low → high"
      sortDescLabel="Sort high → low"
      sortDir={sortDir}
      onSort={onSort}
      onClose={onClose}
      onClear={clear}
      onApply={apply}
    >
      <div className="px-3 py-2 border-b border-black/5">
        <div className="text-[10px] uppercase font-bold tracking-[1.2px] text-text-muted mb-1.5">Quick thresholds</div>
        <div className="grid grid-cols-2 gap-1.5">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => pick(p)}
              className="px-2 py-1.5 rounded text-[11px] font-bold border bg-surface-2 text-text-body border-transparent hover:bg-gat-green/10 hover:text-gat-greenDark"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-3 py-2 border-b border-black/5">
        <div className="text-[10px] uppercase font-bold tracking-[1.2px] text-text-muted mb-1.5">Custom range</div>
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-text-muted">min {symbol}</span>
            <input
              type="text"
              inputMode="decimal"
              value={min}
              onChange={setNumeric(setMin)}
              className="w-full bg-surface-3 border border-transparent rounded pl-12 pr-2 py-1 text-[11.5px] focus:outline-none focus:ring-2 focus:ring-gat-green/30"
              placeholder="-"
            />
          </div>
          <span className="text-[10px] text-text-muted">to</span>
          <div className="flex-1 relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-text-muted">max {symbol}</span>
            <input
              type="text"
              inputMode="decimal"
              value={max}
              onChange={setNumeric(setMax)}
              className="w-full bg-surface-3 border border-transparent rounded pl-12 pr-2 py-1 text-[11.5px] focus:outline-none focus:ring-2 focus:ring-gat-green/30"
              placeholder="-"
            />
          </div>
        </div>
      </div>
    </FilterPopoverShell>
  );
}
